import { type QueryFunctionContext } from "@tanstack/react-query";
import { type CardReportingItem } from "components/cards/CardReporting/CardReporting";
import { type LinechartProps } from "components/charts";
import { type ColumnDataPoint } from "components/charts/types/ColumnChart";
import {
  type PerformanceFilter,
  type PerformanceWithAccountFilter,
  fallbackToZero,
  getRateString,
  lineChartFactory,
  longFormDateFilter,
  sendExportRequest,
} from "features/Library/Common/utils/performanceUtils";
import { roundToTwoDigits } from "features/Library/Common/utils/roundToTwoDigits";
import { peopleReportingService } from "features/Reporting/services/peopleReportingService";
import moment from "moment";
import { type PaginationParams } from "features/Library/Common/models";
import { type FetchPageResult } from "features/Reporting/Content/shared/GenericPerformanceListWithServerPagination";
import { type PeopleOverviewUsersItem, type PeopleOverviewAccountsItem } from "features/Reporting/types/people";

const countHeaderName = process.env.REACT_APP_COUNT_HEADER_NAME as string;

export const yearDateRange = (): PerformanceFilter => {
  const today = moment().startOf("day").toISOString();
  const todayMinus30 = moment().subtract(1, "year").startOf("month").startOf("day").toISOString();
  return longFormDateFilter(todayMinus30, today);
};

type QueryContext = QueryFunctionContext<[_name: string, dateFilter: PerformanceWithAccountFilter], AbortSignal>;

type LineResponse = Pick<LinechartProps, "xData" | "yData">;

// For now, these are 2 separate charts
export const fetchPeopleOverviewLineCharts = async ({
  queryKey,
  signal,
}: QueryContext): Promise<{
  activeUsers: LineResponse;
  interactionsAsBarData: ColumnDataPoint[];
  activeUsersTooltips: string[];
}> => {
  const { data } = await peopleReportingService.overview.getLineChart(
    {
      ...queryKey[1],
    },
    signal!,
  );

  const lineChartData = lineChartFactory(data);

  return {
    activeUsers: { xData: [lineChartData.Date], yData: [lineChartData.ActiveUsersCount] },
    interactionsAsBarData: data.map((d) => ({ id: d.Date, category: d.Date, value: d.InteractionCount })),
    activeUsersTooltips: data.map(
      (d) => `${roundToTwoDigits(d.ActiveUsersCount)} (${Math.round(d.LicenseAssignedRate * 100)}% of licensed)`,
    ),
  };
};

export const fetchPeopleOverviewStatistics = async ({
  queryKey,
  signal,
}: QueryContext): Promise<CardReportingItem[]> => {
  const { data } = await peopleReportingService.overview.getStatistics({ ...queryKey[1] }, signal!);

  return [
    {
      stat: fallbackToZero(data.ActiveUserCount),
      popupBody: "Total number of distinct active users in this account during the specified time period.",
      statDescription: "Active Users",
    },
    {
      stat: getRateString(data.LicenseAssignedRate),
      popupBody: "The percentage of licensed users who have interacted with content during the specified time period.",
      statDescription: "Percent Licensed",
    },

    {
      stat: fallbackToZero(data.InteractionCount),
      popupBody:
        "Total number of platform interactions for all participating users across all interaction types during the specified time period.",
      statDescription: "Interactions",
    },
    {
      stat: fallbackToZero(data.AvgInteractionPerUser),
      popupBody: "The average number of interactions each user had during the specified time period.",
      statDescription: "Avg. Interactions / User",
    },
  ];
};

export const fetchPeopleOverviewAccountsTable = async ({ queryKey, signal }: QueryContext) => {
  const { data } = await peopleReportingService.overview.getAccountsTable({ ...queryKey[1] }, undefined, signal);

  // Add id field for table keys
  return data.map((d) => ({ ...d, id: d.AccountId }));
};

export const fetchPeopleOverviewAccountsTablePaginated = async (
  filter: PerformanceWithAccountFilter,
  pagination: PaginationParams,
  signal?: AbortSignal,
): Promise<FetchPageResult<PeopleOverviewAccountsItem>> => {
  const response = await peopleReportingService.overview.getAccountsTable(filter, pagination, signal);

  const responseWithKey = response.data.map((account) => ({ ...account, id: account.AccountId }));

  return {
    rows: responseWithKey,
    totalCount: Number.parseInt(response.headers[countHeaderName]),
  };
};

export const fetchPeopleOverviewUsersTable = async ({ queryKey, signal }: QueryContext) => {
  const { data } = await peopleReportingService.overview.getUsersTable({ ...queryKey[1] }, undefined, signal);

  // Add id field for table keys
  return data.map((d) => ({ ...d, id: d.UserId }));
};

export const fetchPeopleOverviewUsersTablePaginated = async (
  filter: PerformanceWithAccountFilter,
  pagination: PaginationParams,
  signal?: AbortSignal,
): Promise<FetchPageResult<PeopleOverviewUsersItem>> => {
  const response = await peopleReportingService.overview.getUsersTable(filter, pagination, signal);

  const responseWithKey = response.data.map((user) => ({ ...user, id: user.UserId }));

  return {
    rows: responseWithKey,
    totalCount: Number.parseInt(response.headers[countHeaderName]),
  };
};

let abortController = new AbortController();

export const fetchPeopleOverviewAccountsTableExport = (filterParams: PerformanceWithAccountFilter) => {
  return sendExportRequest(
    (abortController) => peopleReportingService.overview.getAccountsTableExport(filterParams, abortController.signal),
    "Accounts",
    abortController,
  );
};

export const fetchPeopleOverviewUsersTableExport = (filterParams: PerformanceWithAccountFilter) => {
  return sendExportRequest(
    (abortController) => peopleReportingService.overview.getUsersTableExport(filterParams, abortController.signal),
    "Users",
    abortController,
  );
};

export const sharedOverviewLineProps = {
  xFormatterFunc: (date: Date) => moment(date).format("MMM 'YY"),
  tooltipFormatter: (date: Date, value: number) =>
    `${moment(date).format("MMM 'YY")}: ${roundToTwoDigits(value)} Interactions`,
} as const;
